"use client";
import type { DocumentNode } from "graphql";
import { bleedBanner, bleedBannerDataFragmentDoc } from "@gql/graphql";
import { Banner } from "@nrldigitaltechnology/ui-community";
import {
  BannerDataProps,
  BannerVariantProps,
} from "@nrldigitaltechnology/ui-community/dist/components/Banner/types";
import { AnyProps } from "@/utils/types";
import { useContext, useEffect, useState } from "react";
import { HeaderContext } from "@/utils/providers/HeaderProvider";
import { useWindowDimension } from "@/hooks/useWindowDimension";

type bleedBannerCMSProps = {
  data: bleedBanner | null;
  inEditMode?: boolean;
};

type CardActionProps = {
  id: string;
  label: string;
  actionUrl?: string;
  data?: AnyProps;
  actionCb?: (action: CardActionProps, data?: AnyProps) => void;
};

// Extend the component type to include the static getDataFragment method
interface bleedBannerComponentProps extends React.FC<bleedBannerCMSProps> {
  getDataFragment: () => [string, DocumentNode];
}

const BleedBanner: bleedBannerComponentProps = ({
  data,
  inEditMode = false,
}: bleedBannerCMSProps) => {
  // Destructure with safe defaults

  const [bannerOpen, setBannerOpen] = useState(true)
  const {
    backgroundImage,
    title,
    text,
    logo,
    ctaButtonText,
    ctaUrl,
    bannerVariants,
  } = data || {};

  const headerRef = useContext(HeaderContext);
  const [headerHeight, setHeaderHeight] = useState(0);
  const dimension = useWindowDimension();

  useEffect(() => {
    if (headerRef?.current) {
      setHeaderHeight(headerRef?.current?.offsetHeight);
    }
  }, [dimension?.width]);

  const dataMapping: BannerDataProps = {
    bannerImg:
      (bannerVariants !== "headerBanner"
        ? backgroundImage?.url?.default
        : "") || "",
    logo: logo?.url?.default || "",
    title: title || "",
    description: text || "",
    actions: ctaButtonText
      ? [
        {
          id: ctaUrl?.default,
          label: ctaButtonText || "",
          actionUrl: ctaUrl?.default || "",
          actionCb: (val: CardActionProps) => {
            window.open(val?.actionUrl || "", "_blank")?.focus();
          },
        },
      ]
      : [],
  };

  function BannerVariants(bannerVariants: string): BannerVariantProps {
    switch (bannerVariants) {
      case "narrowBleed":
        return "fullBleedNarrow";
      case "headerBanner":
        return "headerBanner";
      case "wideBleed":
        return "fullBleedWide";
      default:
        return undefined;
    }
  }

  return (
    <>
      {bannerVariants === "headerBanner" ? (
        <div className={`absolute inset-x-0`} style={{ top: headerHeight }}>
          {
            bannerOpen &&
            <Banner
              bannerData={dataMapping}
              customStyles={{ imageLogoStyle: "w-9 h-9" }}
              variant={BannerVariants(bannerVariants || "")}
              onCloseBanner={(val) => setBannerOpen(val)}
            />
          }
        </div>
      ) : bannerVariants === "wideBleed" ?  
      <Banner
            bannerData={dataMapping}
            customStyles={{titleStyle: "text-[42px]",
               containerStyle: "w-full flex justify-center",
                imageLogoStyle: "w-12 h-12" , contentContainerStyle:'max-w-[700px] bottom-20 left-20' }}
            variant={BannerVariants(bannerVariants || "")}
            enableBannerGradient={true}
          />

      : (
        <div className="flex justify-center my-16 ">
          <Banner
            bannerData={dataMapping}
            // customStyles={{titleStyle: "text-[42px]",
            //    containerStyle: "w-full flex justify-center",
            //     imageLogoStyle: "w-12 h-12" , contentContainerStyle:'max-w-[700px] bottom-20 left-20' }}
            variant={BannerVariants(bannerVariants || "")}
            enableBannerGradient={true}
          />
        </div>
      )}
    </>
  );
};

BleedBanner.displayName = "Banner";

BleedBanner.getDataFragment = () => [
  "bleedBannerDataProps",
  bleedBannerDataFragmentDoc,
];

// Attach the data fragment to the component
BleedBanner.getDataFragment = (): [string, DocumentNode] => [
  "bleedBannerDataProps",
  bleedBannerDataFragmentDoc,
];

export default BleedBanner;
